<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  qualityScores: {
    type: Object,
    default: () => null,
  },
});

const { t } = useI18n();

const isExpanded = ref(false);

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const scores = computed(() => {
  if (!props.qualityScores) return null;
  return props.qualityScores;
});

const compositeScore = computed(() => {
  return scores.value?.composite_score ?? null;
});

const DIMENSION_KEYS = [
  'resolution',
  'accuracy',
  'tone',
  'efficiency',
  'tool_usage',
  'safety',
];

const dimensions = computed(() => {
  if (!scores.value?.dimensions) return [];
  const dims = scores.value.dimensions;
  return DIMENSION_KEYS.map(key => ({
    key,
    label: t(
      `CONVERSATION_SIDEBAR.CAPTAIN_QUALITY_BADGE.DIMENSIONS.${key.toUpperCase()}`
    ),
    value: dims[key],
  })).filter(d => d.value != null);
});

const issues = computed(() => {
  return scores.value?.issues ?? [];
});

const verdict = computed(() => {
  return scores.value?.verdict ?? '';
});

const VERDICT_I18N_MAP = {
  excellent: 'EXCELLENT',
  good: 'GOOD',
  acceptable: 'ACCEPTABLE',
  needs_improvement: 'NEEDS_IMPROVEMENT',
  critical: 'CRITICAL',
};

const verdictLabel = computed(() => {
  const i18nKey = VERDICT_I18N_MAP[verdict.value];
  if (!i18nKey) return verdict.value;
  return t(`CONVERSATION_SIDEBAR.CAPTAIN_QUALITY_BADGE.VERDICTS.${i18nKey}`);
});

const scoreLabel = computed(() => {
  if (compositeScore.value == null) return '';
  return t('CONVERSATION_SIDEBAR.CAPTAIN_QUALITY_BADGE.SCORE_LABEL', {
    score: compositeScore.value.toFixed(1),
  });
});

/**
 * Returns Tailwind classes for the badge based on score tier.
 * Uses Radix n- color tokens available in the project theme:
 *   Excellent (8.5+) = teal, Good (7-8.4) = blue,
 *   Acceptable (5.5-6.9) = amber, Below = ruby
 */
const badgeClasses = computed(() => {
  const score = compositeScore.value;
  if (score == null) return '';
  if (score >= 8.5) return 'bg-n-teal-3 text-n-teal-11 border-n-teal-5';
  if (score >= 7) return 'bg-n-blue-3 text-n-blue-11 border-n-blue-5';
  if (score >= 5.5) return 'bg-n-amber-3 text-n-amber-11 border-n-amber-5';
  return 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-5';
});

const verdictClasses = computed(() => {
  const v = verdict.value;
  if (v === 'excellent') return 'text-n-teal-11';
  if (v === 'good') return 'text-n-blue-11';
  if (v === 'acceptable') return 'text-n-amber-11';
  return 'text-n-ruby-11';
});

/**
 * Returns Tailwind background class for a dimension progress bar.
 */
const barColor = score => {
  if (score >= 8.5) return 'bg-n-teal-9';
  if (score >= 7) return 'bg-n-blue-9';
  if (score >= 5.5) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
};

const hasScores = computed(() => {
  return compositeScore.value != null;
});
</script>

<template>
  <div v-if="hasScores" class="flex flex-col gap-2">
    <!-- Clickable badge pill -->
    <button
      class="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium cursor-pointer transition-colors"
      :class="badgeClasses"
      @click="toggleExpanded"
    >
      <span>{{ scoreLabel }}</span>
      <span :class="verdictClasses" class="font-semibold">
        {{ verdictLabel }}
      </span>
      <fluent-icon
        :icon="isExpanded ? 'chevron-up' : 'chevron-down'"
        size="12"
        class="ml-0.5"
      />
    </button>

    <!-- Expanded detail panel -->
    <div
      v-if="isExpanded"
      class="rounded-lg border border-n-weak bg-n-background p-3 flex flex-col gap-3"
    >
      <!-- Dimension scores -->
      <div v-if="dimensions.length" class="flex flex-col gap-2">
        <div
          v-for="dim in dimensions"
          :key="dim.key"
          class="flex items-center gap-2"
        >
          <span class="text-xs text-n-slate-11 w-20 shrink-0 truncate">
            {{ dim.label }}
          </span>
          <div class="flex-1 h-1.5 rounded-full bg-n-slate-4 overflow-hidden">
            <div
              class="h-full rounded-full transition-all"
              :class="barColor(dim.value)"
              :style="{ width: `${(dim.value / 10) * 100}%` }"
            />
          </div>
          <span class="text-xs font-medium text-n-slate-12 w-7 text-right">
            {{ dim.value.toFixed(1) }}
          </span>
        </div>
      </div>

      <!-- Issues list -->
      <div v-if="issues.length" class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-12">
          {{ $t('CONVERSATION_SIDEBAR.CAPTAIN_QUALITY_BADGE.ISSUES_TITLE') }}
        </span>
        <ul class="list-disc list-inside">
          <li
            v-for="(issue, idx) in issues"
            :key="idx"
            class="text-xs text-n-slate-11 leading-relaxed"
          >
            {{ issue }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
